/**
 * Design tokens — the single source of truth for colour in the application.
 *
 * Derived from docs/architecture/design-tokens.md. SPEC 13.6 forbids brand
 * values in components, and that rule only holds if there is exactly one place
 * they live. Every value here carries its verified contrast ratio in the source
 * document; changing one means re-checking it there.
 */

export const light = {
  // surfaces — warm paper, never pure white
  'surface-base': '#FAF7F2',
  'surface-raised': '#FFFFFF',
  'surface-sunken': '#F2ECE3',
  'border-subtle': '#E4DCD1',
  'border-strong': '#C9BCAC',

  // text
  'text-primary': '#2A2420',
  'text-secondary': '#6B5F56',
  'text-disabled': '#9A8F84',

  // brand
  primary: '#A8442A',
  'primary-hover': '#8E3722',
  'primary-wash': '#F7E8E2',

  // honey. Fails normal-text contrast on white and takes DARK text.
  // The easiest token in the set to misuse.
  accent: '#C8862A',

  // status
  success: '#2F6B4F',
  warning: '#8A5A16',
  error: '#B3261E',

  // deliberately the one cool colour, so focus reads as system chrome
  focus: '#1F5FA8',
} as const

export const dark = {
  'surface-base': '#1A1614',
  'surface-raised': '#241F1B',
  'surface-sunken': '#120F0E',
  'border-subtle': '#332B26',
  'border-strong': '#4D423A',

  'text-primary': '#F2EDE6',
  'text-secondary': '#B5A99D',
  'text-disabled': '#7A6E64',

  primary: '#E08A6B',
  'primary-hover': '#EDA084',
  'primary-wash': '#2E211B',

  accent: '#E0AC5C',

  success: '#6FBF94',
  warning: '#D9A441',
  error: '#F2938C',

  focus: '#7FB2F0',
} as const

/** 4px base. Arbitrary values are how layouts drift. */
export const space = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
  7: '48px',
  8: '64px',
} as const

/** Rounded, not bubbly. radius-md carries most of the interface. */
export const radius = {
  sm: '6px',
  md: '12px',
  lg: '20px',
  full: '9999px',
} as const
